import { useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Keyboard, Pagination } from "swiper";
import { Link } from "react-router-dom";

const isVisibleSlide = (slide) => {
  if (!slide || slide.is_active === false || (!slide.image && !slide.mobile_image)) return false;

  const now = Date.now();
  const startsAt = slide.starts_at ? new Date(slide.starts_at).getTime() : null;
  const endsAt = slide.ends_at ? new Date(slide.ends_at).getTime() : null;

  if (startsAt && Number.isFinite(startsAt) && startsAt > now) return false;
  if (endsAt && Number.isFinite(endsAt) && endsAt < now) return false;

  return true;
};

const isFullImageSlide = (slide) =>
  ["full_image", "full", "image"].includes(String(slide?.display_mode || "").trim());

const hasTextContent = (slide) =>
  !isFullImageSlide(slide) && Boolean(slide?.title || slide?.subtitle || slide?.button_text);

const SlideInner = ({ slide, index }) => {
  const image = slide.image || slide.mobile_image || "";
  const mobileImage = slide.mobile_image || image;
  const fullImage = isFullImageSlide(slide);
  const hasText = hasTextContent(slide);
  const overlayOpacity = Math.max(0, Math.min(Number(slide.overlay_opacity ?? 0.28), 0.85));

  return (
    <>
      <div className="slide-img">
        <picture>
          {mobileImage && <source media="(max-width: 720px)" srcSet={mobileImage} />}
          <img
            src={image}
            alt={slide.title || "بنر جوتی"}
            loading={index === 0 ? "eager" : "lazy"}
            fetchPriority={index === 0 ? "high" : "auto"}
            decoding="async"
          />
        </picture>
      </div>

      {hasText && (
        <div className="slide-overlay" style={{ "--slide-overlay-opacity": overlayOpacity }}>
          <div className="site-container">
            <div className="hero-content">
              <span className="hero-badge">Jooti Collection</span>
              {slide.title && <h1>{slide.title}</h1>}
              {slide.subtitle && <p>{slide.subtitle}</p>}
              {slide.button_text && (
                <div className="hero-actions">
                  <span className="hero-primary-btn hero-primary-btn-static">{slide.button_text}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {fullImage && <span className="sr-only">{slide.title || "بنر تبلیغاتی جوتی"}</span>}
    </>
  );
};

const Slider = ({ slides = [] }) => {
  const activeSlides = useMemo(() => slides.filter(isVisibleSlide), [slides]);

  if (!activeSlides.length) {
    return null;
  }

  const hasMultipleSlides = activeSlides.length > 1;

  return (
    <section className="hero-slider" aria-label="بنرهای صفحه اصلی">
      <div className="site-container hero-slider-container">
        <Swiper
          pagination={hasMultipleSlides ? { clickable: true, dynamicBullets: false } : false}
          autoplay={
            hasMultipleSlides
              ? {
                  delay: 4200,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
              : false
          }
          modules={[Pagination, Autoplay, Keyboard]}
          className="mySwiper jooti-home-slider"
          loop={hasMultipleSlides}
          speed={720}
          grabCursor={hasMultipleSlides}
          keyboard={hasMultipleSlides ? { enabled: true } : false}
        >
          {activeSlides.map((slide, index) => {
            const fullImage = isFullImageSlide(slide);
            const content = <SlideInner slide={slide} index={index} />;
            const link = slide.button_link || "";
            const className = `hero-slide-link ${fullImage ? "hero-slide-full-image" : "hero-slide-text-image"}`;

            return (
              <SwiperSlide className="slide" key={slide.id || `${slide.title}-${index}`}>
                {link ? (
                  <Link to={link} className={className} aria-label={slide.title || "مشاهده بنر جوتی"}>
                    {content}
                  </Link>
                ) : (
                  <div className={className}>
                    {content}
                  </div>
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>

      </div>
    </section>
  );
};

export default Slider;
